import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Database, Plus, Pencil, Archive, Store, Trash2, Package, Factory, Check, X, Info, Layers, Sprout, MapPin, Crosshair, Loader2, Search } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { CATEGORY_COLORS, fmtRp, fmtUnit } from "../lib/constants";
import { ProductAvatar, BranchBadge, ImageUpload } from "../components/visual";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { useT } from "../lib/i18n";

const TABS = ["Products", "Branches", "Categories", "Units", "Suppliers"];
const SIMPLE_LABEL = { category: "Category", unit: "Unit", supplier: "Supplier" };

// Step 4B: product classification → auto visibility presets (overridable).
const CLASSIFICATION = {
  RAW_MATERIAL: {
    label: "Central Raw Material", tone: "info", icon: Package,
    preset: { visible_central_procurement: true, visible_central_inventory: true, visible_recipe_bom: true, visible_branch_ordering: false },
  },
  INTERNAL_FINISHED_GOOD: {
    label: "Central Production Product", tone: "success", icon: Factory,
    preset: { visible_central_procurement: false, visible_central_inventory: true, visible_recipe_bom: true, visible_branch_ordering: true },
  },
  BRANCH_SKU: {
    label: "Branch Product", tone: "warn", icon: Store,
    preset: { visible_central_procurement: false, visible_central_inventory: false, visible_recipe_bom: false, visible_branch_ordering: true },
  },
  PREPARED: {
    label: "Ready-to-Cook Ingredient", tone: "success", icon: Sprout,
    preset: { visible_central_procurement: false, visible_central_inventory: true, visible_recipe_bom: true, visible_branch_ordering: false },
  },
};

// Visibility modules: `label` = checkbox toggle, `short` = card bullet.
const VIS_MODULES = [
  { key: "visible_central_procurement", label: "Show in Central Procurement", short: "Central Procurement" },
  { key: "visible_central_inventory", label: "Show in Central Inventory", short: "Central Inventory" },
  { key: "visible_recipe_bom", label: "Show in Central Production", short: "Central Production" },
  { key: "visible_branch_ordering", label: "Show in Branch Ordering", short: "Branch Ordering" },
];

export default function MasterData() {
  const { isRole } = useAuth();
  const branding = useBranding();
  const toast = useToast();
  const { t } = useT();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab");
  const [tab, setTab] = useState(["Products", "Branches", "Categories", "Units", "Suppliers", "Company Profile"].includes(initialTab) ? initialTab : "Products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  // Products toolbar state (Master Data usability upgrade)
  const [pSearch, setPSearch] = useState("");
  const [pCategoryFilter, setPCategoryFilter] = useState("ALL");
  const [pClassFilter, setPClassFilter] = useState("ALL");
  const [pSort, setPSort] = useState("category_asc");
  const [company, setCompany] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);
  const [freshCatId, setFreshCatId] = useState(null);
  const [gpsBusy, setGpsBusy] = useState(false);

  const canEdit = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "OWNER");
  const canEditCompany = isRole("SUPER_ADMIN", "OWNER");
  const visibleTabs = canEditCompany ? [...TABS, "Company Profile"] : TABS;

  const load = async () => {
    const [p, c, u, s, b] = await Promise.all([
      api.get("/products"), api.get("/categories"), api.get("/units"), api.get("/suppliers"), api.get("/branches"),
    ]);
    setProducts(p.data); setCategories(c.data); setUnits(u.data); setSuppliers(s.data); setBranches(b.data); setLoading(false);
    api.get("/fresh-market-category").then(({ data }) => setFreshCatId(data.category_id)).catch(() => {});
    if (canEditCompany) api.get("/settings").then(({ data }) => setCompany(data.company_profile || {})).catch(() => {});
  };
  useEffect(() => { load();
    // eslint-disable-next-line
  }, []);

  const setFreshCategory = async (id) => {
    const next = freshCatId === id ? null : id;
    try {
      await api.put("/fresh-market-category", { category_id: next });
      setFreshCatId(next);
      toast.success(next ? t("Fresh Market category set") : t("Fresh Market category cleared"));
    } catch (e) { toast.error(t("Failed")); }
  };
  useEffect(() => {
    const qt = new URLSearchParams(location.search).get("tab");
    if (qt && ["Products", "Branches", "Categories", "Units", "Suppliers", "Company Profile"].includes(qt)) setTab(qt);
  }, [location.search]);
  if (loading) return <PageLoader />;

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put("/settings", { company_profile: company });
      toast.success(t("Company profile updated"));
      branding.refresh();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSavingCompany(false); }
  };

  const openProduct = (p) => {
    setForm(p ? { ...p } : { name: "", category_id: categories[0]?.id, unit: units[0]?.name, inventory_type: "STOCKED", product_type: "RAW_MATERIAL", minimum_stock: 0, ideal_stock: 0, critical_threshold_pct: 50, track_inventory: true, active: true, price: 0, transfer_price: 0, branch_selling_price: 0, supplier_id: suppliers[0]?.id, image: null, visible_central_procurement: true, visible_central_inventory: true, visible_recipe_bom: true, visible_branch_ordering: false });
    setModal("product");
  };
  const saveProduct = async () => {
    try {
      if (form.id) await api.put(`/products/${form.id}`, form);
      else await api.post("/products", form);
      toast.success(t("Product saved")); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };
  const archive = async (p) => { await api.delete(`/products/${p.id}`); toast.success(t("Archived")); load(); };

  // Multi-supplier helpers (product form)
  const addSupplierRow = () => setForm((f) => ({ ...f, suppliers: [...(f.suppliers || []), { supplier_id: "", price: 0, preferred: !(f.suppliers || []).length }] }));
  const setSupplierRow = (i, patch) => setForm((f) => ({ ...f, suppliers: (f.suppliers || []).map((s, idx) => idx === i ? { ...s, ...patch } : s) }));
  const setPreferredSupplier = (i) => setForm((f) => ({ ...f, suppliers: (f.suppliers || []).map((s, idx) => ({ ...s, preferred: idx === i })) }));
  const removeSupplierRow = (i) => setForm((f) => ({ ...f, suppliers: (f.suppliers || []).filter((_, idx) => idx !== i) }));
  const hardDelete = async (p) => {
    if (!window.confirm(`${t("Permanently delete")} "${p.name}"? ${t("This cannot be undone.")}`)) return;
    try { await api.delete(`/products/${p.id}?hard=true`); toast.success(t("Product deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const saveSimple = async () => {
    try {
      const editing = !!form.id;
      if (modal === "category") {
        const body = { name: form.name, color_key: form.color_key || "packaging" };
        if (editing) await api.put(`/categories/${form.id}`, body); else await api.post("/categories", body);
      }
      if (modal === "unit") {
        const body = { name: form.name };
        if (editing) await api.put(`/units/${form.id}`, body); else await api.post("/units", body);
      }
      if (modal === "supplier") {
        const body = { name: form.name, contact: form.contact, phone: form.phone, address: form.address };
        if (editing) await api.put(`/suppliers/${form.id}`, body); else await api.post("/suppliers", body);
      }
      toast.success(t("Saved")); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openCategory = (c) => { setForm(c ? { ...c } : { name: "", color_key: "packaging" }); setModal("category"); };
  const openUnit = (u) => { setForm(u ? { ...u } : { name: "" }); setModal("unit"); };
  const openSupplier = (s) => { setForm(s ? { ...s } : { name: "", contact: "", phone: "", address: "" }); setModal("supplier"); };
  const removeSimple = async (kind, item) => {
    if (!window.confirm(`${t("Remove")} "${item.name}"?`)) return;
    try { await api.delete(`/${kind}/${item.id}`); toast.success(t("Removed")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openBranch = (b) => { setForm(b ? { ...b } : { name: "", code: "", address: "", logo: null, lat: null, lng: null, radius_m: 50 }); setModal("branch"); };
  const useCurrentLocation = () => {
    if (!navigator.geolocation) { toast.error(t("GPS not supported on this device")); return; }
    setGpsBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, lat: Number(pos.coords.latitude.toFixed(6)), lng: Number(pos.coords.longitude.toFixed(6)) })); setGpsBusy(false); toast.success(t("Location captured")); },
      () => { setGpsBusy(false); toast.error(t("Unable to get location. Enable GPS and allow permission.")); },
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 }
    );
  };
  const saveBranch = async () => {
    const payload = {
      name: form.name, code: form.code, address: form.address, logo: form.logo,
      lat: form.lat === "" || form.lat == null ? null : Number(form.lat),
      lng: form.lng === "" || form.lng == null ? null : Number(form.lng),
      radius_m: form.radius_m === "" || form.radius_m == null ? null : Number(form.radius_m),
    };
    try {
      if (form.id) await api.put(`/branches/${form.id}`, payload);
      else await api.post("/branches", payload);
      toast.success(form.id ? t("Branch updated") : t("Branch created")); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title={t("Master Data")} subtitle={t("Products, categories, units & suppliers")} icon={Database} />
      <div className="mb-5 flex flex-wrap gap-2">
        {visibleTabs.map((tabName) => (
          <button key={tabName} onClick={() => setTab(tabName)} data-testid={`md-tab-${tabName}`}
            className={`rounded-xl px-4 py-2 text-sm font-semibold ${tab === tabName ? "bg-primary text-white" : "bg-white border border-line text-ink-soft"}`}>{t(tabName)}</button>
        ))}
      </div>

      {tab === "Products" && (
        <>
          {canEdit && <div className="mb-3"><Button onClick={() => openProduct(null)} data-testid="add-product"><Plus size={18} /> {t("Add Product")}</Button></div>}
          {products.length === 0 ? (
            <EmptyState title={t("No products yet")} subtitle={t("Add your first product to start building your master data.")}
              action={canEdit ? <Button onClick={() => openProduct(null)} data-testid="empty-add-product"><Plus size={18} /> {t("Add Product")}</Button> : null} />
          ) : (
          <ProductsCatalog
            products={products} categories={categories} canEdit={canEdit}
            search={pSearch} setSearch={setPSearch}
            categoryFilter={pCategoryFilter} setCategoryFilter={setPCategoryFilter}
            classFilter={pClassFilter} setClassFilter={setPClassFilter}
            sort={pSort} setSort={setPSort}
            openProduct={openProduct} archive={archive} hardDelete={hardDelete}
            t={t}
          />
          )}
        </>
      )}

      {tab === "Branches" && (
        branches.length === 0 ? (
          <EmptyState title={t("No branches yet")} subtitle={t("Create your first branch to start operating.")}
            action={canEdit ? <Button onClick={() => openBranch(null)} data-testid="empty-add-branch"><Plus size={18} /> {t("Add Branch")}</Button> : null} />
        ) : (
        <>
          {canEdit && <div className="mb-3"><Button onClick={() => openBranch(null)} data-testid="add-branch"><Plus size={18} /> {t("Add Branch")}</Button></div>}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            {branches.map((b) => (
              <Card key={b.id} className="flex items-center justify-between p-4" data-testid={`branch-item-${b.name}`}>
                <BranchBadge branch={b} size="lg" showCode />
                {canEdit && (
                  <Button variant="outline" size="sm" onClick={() => openBranch(b)} data-testid={`edit-branch-${b.name}`}>
                    <Pencil size={14} /> {t("Logo")}
                  </Button>
                )}
              </Card>
            ))}
          </div>
        </>
        )
      )}

      {tab === "Categories" && (
        <div>
          <p className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-xs font-medium text-emerald-800" data-testid="fresh-market-hint">
            <Sprout size={15} /> {t("Tap the leaf to mark which category is the Fresh Market category. Its products route to Fresh Market purchasing; all others go to Central Warehouse.")}
          </p>
          <SimpleList items={categories} canEdit={canEdit} t={t} onAdd={() => openCategory(null)} onEdit={openCategory} onRemove={(c) => removeSimple("categories", c)} testid="category"
            emptyTitle={t("No categories yet")} emptySubtitle={t("Add your first category to classify your products.")}
            render={(c) => (
              <div className="flex items-center gap-2">
                {canEdit && (
                  <button onClick={() => setFreshCategory(c.id)} title={t("Set as Fresh Market category")} data-testid={`fresh-toggle-${c.name}`}
                    className={`shrink-0 rounded-lg p-1 transition ${freshCatId === c.id ? "bg-emerald-500 text-white" : "text-stone-300 hover:bg-stone-100 hover:text-emerald-500"}`}>
                    <Sprout size={15} />
                  </button>
                )}
                <span className="rounded-full px-2.5 py-1 text-xs font-bold" style={{ background: (CATEGORY_COLORS[c.color_key] || CATEGORY_COLORS.packaging).bg, color: (CATEGORY_COLORS[c.color_key] || CATEGORY_COLORS.packaging).text }}>{c.name}</span>
                {freshCatId === c.id && <Badge tone="success" data-testid={`fresh-badge-${c.name}`}>{t("Fresh Market")}</Badge>}
              </div>
            )} />
        </div>
      )}
      {tab === "Units" && <SimpleList items={units} canEdit={canEdit} t={t} onAdd={() => openUnit(null)} onEdit={openUnit} onRemove={(u) => removeSimple("units", u)} render={(u) => <span className="font-bold uppercase tracking-wider text-ink">{u.name}</span>} testid="unit" emptyTitle={t("No units yet")} emptySubtitle={t("Add your first unit of measure (e.g. kg, pcs, liter).")} />}
      {tab === "Suppliers" && <SimpleList items={suppliers} canEdit={canEdit} t={t} onAdd={() => openSupplier(null)} onEdit={openSupplier} onRemove={(s) => removeSimple("suppliers", s)} render={(s) => <div><p className="font-semibold">{s.name}</p><p className="text-xs text-ink-mute">{s.phone} · {s.contact}</p></div>} testid="supplier" emptyTitle={t("No suppliers yet")} emptySubtitle={t("Add your first supplier to start procurement.")} />}

      {tab === "Company Profile" && canEditCompany && (
        <Card className="max-w-2xl p-5" data-testid="company-profile-card">
          <p className="mb-4 text-sm text-ink-soft">{t("These details appear on all documents (Surat Jalan, Faktur) and reports.")}</p>
          {!company ? <PageLoader /> : (
            <>
              <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-line p-4" data-testid="cp-app-logo-box">
                  <ImageUpload label={t("App Logo")} value={company.app_logo} onChange={(img) => setCompany({ ...company, app_logo: img })} shape="square" testid="cp-app-logo" />
                  <p className="mt-2 text-xs text-ink-mute">{t("Shown in the sidebar, header & login screen.")}</p>
                </div>
                <div className="rounded-2xl border border-line p-4" data-testid="cp-doc-logo-box">
                  <ImageUpload label={t("Document Logo")} value={company.logo} onChange={(img) => setCompany({ ...company, logo: img })} shape="square" testid="cp-logo" />
                  <p className="mt-2 text-xs text-ink-mute">{t("Shown on Surat Jalan & Faktur PDFs.")}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div><label className="cf-label">{t("Company Name")}</label><input value={company.company_name || ""} onChange={(e) => setCompany({ ...company, company_name: e.target.value })} className="cf-input" data-testid="cp-company-name" /></div>
                <div><label className="cf-label">{t("Warehouse Name")}</label><input value={company.warehouse_name || ""} onChange={(e) => setCompany({ ...company, warehouse_name: e.target.value })} className="cf-input" data-testid="cp-warehouse-name" /></div>
                <div className="sm:col-span-2"><label className="cf-label">{t("Address")}</label><input value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} className="cf-input" data-testid="cp-address" /></div>
                <div><label className="cf-label">{t("Phone")}</label><input value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} className="cf-input" data-testid="cp-phone" /></div>
                <div><label className="cf-label">{t("Email")}</label><input value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} className="cf-input" data-testid="cp-email" /></div>
                <div className="sm:col-span-2"><label className="cf-label">{t("Tax Number (NPWP)")}</label><input value={company.tax_number || ""} onChange={(e) => setCompany({ ...company, tax_number: e.target.value })} className="cf-input" data-testid="cp-tax" /></div>
              </div>
              <div className="mt-5 flex justify-end">
                <Button onClick={saveCompany} loading={savingCompany} data-testid="cp-save">{t("Save Profile")}</Button>
              </div>
            </>
          )}
        </Card>
      )}

      {/* Product modal */}
      <Modal open={modal === "product"} onClose={() => setModal(null)} title={form.id ? t("Edit Product") : t("New Product")} size="lg"
        footer={<><Button variant="outline" onClick={() => setModal(null)}>{t("Cancel")}</Button><Button onClick={saveProduct} data-testid="save-product">{t("Save")}</Button></>}>
        <div className="mb-4">
          <ImageUpload label={t("Product Image")} value={form.image} onChange={(img) => setForm({ ...form, image: img })} shape="square" testid="pf-image" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="cf-label">{t("Name")}</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="pf-name" /></div>
          <ClassificationVisibility form={form} setForm={setForm} products={products} t={t} />
          <Select label={t("Category")} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} data-testid="pf-category">
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select label={t("Unit")} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="pf-unit">
            {units.map((u) => <option key={u.id} value={u.name}>{u.name}</option>)}
          </Select>
          <Select label={t("Inventory Type")} value={form.inventory_type} onChange={(e) => setForm({ ...form, inventory_type: e.target.value })} data-testid="pf-type">
            <option value="STOCKED">{t("Stocked")}</option><option value="PASSTHROUGH">{t("Passthrough")}</option>
          </Select>
          <Select label={t("Default Supplier")} value={form.supplier_id || ""} onChange={(e) => setForm({ ...form, supplier_id: e.target.value })}>
            <option value="">—</option>{suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </Select>
          <div><label className="cf-label">{t("Minimum Stock")}</label><input type="number" value={form.minimum_stock} onChange={(e) => setForm({ ...form, minimum_stock: Number(e.target.value) })} className="cf-input" data-testid="pf-min" /></div>
          <div><label className="cf-label">{t("Ideal Stock")}</label><input type="number" value={form.ideal_stock} onChange={(e) => setForm({ ...form, ideal_stock: Number(e.target.value) })} className="cf-input" data-testid="pf-ideal" /></div>
          <div><label className="cf-label">{t("Critical Alert (% of min)")}</label><input type="number" min="1" max="100" value={form.critical_threshold_pct ?? 50} onChange={(e) => setForm({ ...form, critical_threshold_pct: Number(e.target.value) })} className="cf-input" data-testid="pf-critical" /></div>
          <div><label className="cf-label">{t("Default Price (Rp)")}</label><input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="cf-input" data-testid="pf-price" /></div>
          <div><label className="cf-label">{t("Transfer / Selling Price (Rp)")}</label><input type="number" value={form.transfer_price ?? 0} onChange={(e) => setForm({ ...form, transfer_price: Number(e.target.value) })} className="cf-input" data-testid="pf-transfer-price" /></div>
          <div><label className="cf-label">{t("Branch Selling Price")} (Rp)</label><input type="number" value={form.branch_selling_price ?? 0} onChange={(e) => setForm({ ...form, branch_selling_price: Number(e.target.value) })} className="cf-input" data-testid="pf-branch-selling-price" /></div>
          <div><label className="cf-label">{t("Shelf Life (days)")}</label><input type="number" min="0" placeholder={t("optional")} value={form.shelf_life_days ?? ""} onChange={(e) => setForm({ ...form, shelf_life_days: e.target.value === "" ? null : Number(e.target.value) })} className="cf-input" data-testid="pf-shelf-life" /></div>

          {/* Multi-supplier with prices */}
          <div className="col-span-2 rounded-xl border border-line p-3">
            <div className="mb-2 flex items-center justify-between">
              <label className="cf-label mb-0">{t("Suppliers & Prices")}</label>
              <button type="button" onClick={addSupplierRow} className="text-xs font-semibold text-primary" data-testid="pf-add-supplier">{t("+ Add supplier")}</button>
            </div>
            <div className="space-y-2">
              {(form.suppliers || []).map((s, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`pf-supplier-row-${i}`}>
                  <select value={s.supplier_id} onChange={(e) => setSupplierRow(i, { supplier_id: e.target.value })} className="cf-input col-span-6" data-testid={`pf-supplier-${i}`}>
                    <option value="">{t("Select supplier...")}</option>
                    {suppliers.map((sup) => <option key={sup.id} value={sup.id}>{sup.name}</option>)}
                  </select>
                  <input type="number" placeholder={t("Price")} value={s.price} onChange={(e) => setSupplierRow(i, { price: Number(e.target.value) })} className="cf-input col-span-3" data-testid={`pf-supplier-price-${i}`} />
                  <label className="col-span-2 flex items-center gap-1 text-xs font-semibold"><input type="radio" name="pref-supplier" checked={!!s.preferred} onChange={() => setPreferredSupplier(i)} className="accent-primary" data-testid={`pf-supplier-pref-${i}`} /> {t("Pref")}</label>
                  <button type="button" onClick={() => removeSupplierRow(i)} className="col-span-1 text-danger" data-testid={`pf-supplier-remove-${i}`}><Trash2 size={14} /></button>
                </div>
              ))}
              {(!form.suppliers || form.suppliers.length === 0) && <p className="text-xs text-ink-mute">{t("No additional suppliers. The default supplier & price above will be used.")}</p>}
            </div>
          </div>
          <label className="col-span-2 flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.track_inventory} onChange={(e) => setForm({ ...form, track_inventory: e.target.checked })} className="accent-primary" /> {t("Track Inventory")}</label>
          <label className="col-span-2 flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-primary" /> {t("Active")}</label>
        </div>
      </Modal>

      {/* Simple modals */}
      <Modal open={["category", "unit", "supplier"].includes(modal)} onClose={() => setModal(null)} title={`${form.id ? t("Edit") : t("New")} ${t(SIMPLE_LABEL[modal] || "")}`}
        footer={<><Button variant="outline" onClick={() => setModal(null)}>{t("Cancel")}</Button><Button onClick={saveSimple} data-testid="save-simple">{t("Save")}</Button></>}>
        <div className="mb-3"><label className="cf-label">{t("Name")}</label><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="simple-name" /></div>
        {modal === "category" && <Select label={t("Color")} value={form.color_key} onChange={(e) => setForm({ ...form, color_key: e.target.value })}>
          {Object.keys(CATEGORY_COLORS).map((k) => <option key={k} value={k}>{k}</option>)}</Select>}
        {modal === "supplier" && <>
          <div className="mb-3"><label className="cf-label">{t("Contact")}</label><input value={form.contact || ""} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="cf-input" /></div>
          <div className="mb-3"><label className="cf-label">{t("Phone")}</label><input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="cf-input" /></div>
          <div><label className="cf-label">{t("Address")}</label><input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="cf-input" /></div>
        </>}
      </Modal>

      {/* Branch create / logo modal */}
      <Modal open={modal === "branch"} onClose={() => setModal(null)} title={form.id ? `${t("Branch")} · ${form.name || ""}` : t("New Branch")}
        footer={<><Button variant="outline" onClick={() => setModal(null)}>{t("Cancel")}</Button><Button onClick={saveBranch} data-testid="save-branch">{t("Save")}</Button></>}>
        <div className="mb-4">
          <ImageUpload label={t("Branch Logo")} value={form.logo} onChange={(img) => setForm({ ...form, logo: img })} shape="round" testid="branch-logo" />
        </div>
        <div className="mb-3"><label className="cf-label">{t("Branch Name")}</label><input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="cf-input" data-testid="branch-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="cf-label">{t("Code")}</label><input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} className="cf-input" /></div>
          <div><label className="cf-label">{t("Address")}</label><input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className="cf-input" /></div>
        </div>

        {/* Attendance geofence (GPS) — used by the employee attendance check-in/out */}
        <div className="mt-4 rounded-xl border border-line bg-stone-50 p-3" data-testid="branch-geofence">
          <div className="mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-ink"><MapPin size={15} /> {t("Attendance Location (GPS)")}</span>
            <Button type="button" size="sm" variant="outline" onClick={useCurrentLocation} disabled={gpsBusy} data-testid="branch-use-location">
              {gpsBusy ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />} {t("Use Current Location")}
            </Button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="cf-label">{t("Latitude")}</label><input type="number" step="any" value={form.lat ?? ""} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="cf-input" placeholder="-6.15" data-testid="branch-lat" /></div>
            <div><label className="cf-label">{t("Longitude")}</label><input type="number" step="any" value={form.lng ?? ""} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="cf-input" placeholder="106.865" data-testid="branch-lng" /></div>
            <div><label className="cf-label">{t("Radius (m)")}</label><input type="number" value={form.radius_m ?? ""} onChange={(e) => setForm({ ...form, radius_m: e.target.value })} className="cf-input" placeholder="50" data-testid="branch-radius" /></div>
          </div>
          <p className="mt-2 flex items-start gap-1.5 text-[11px] text-ink-mute"><Info size={12} className="mt-0.5 shrink-0" /> {t("Absen ditolak bila koordinat GPS cabang kosong. Kunjungi cabang lalu tekan Gunakan Lokasi Saat Ini. Default radius 50m.")}</p>
        </div>
      </Modal>
    </div>
  );
}

function SimpleList({ items, render, onAdd, onEdit, onRemove, canEdit, testid, t, emptyTitle, emptySubtitle }) {
  return (
    <>
      {canEdit && <div className="mb-3"><Button onClick={onAdd} data-testid={`add-${testid}`}><Plus size={18} /> {t("Add")}</Button></div>}
      {items.length === 0 ? (
        <EmptyState title={emptyTitle} subtitle={emptySubtitle}
          action={canEdit ? <Button onClick={onAdd} data-testid={`empty-add-${testid}`}><Plus size={18} /> {t("Add")}</Button> : null} />
      ) : (
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
        {items.map((it) => (
          <Card key={it.id} className="flex items-center justify-between gap-2 p-4" data-testid={`${testid}-item`}>
            <div className="min-w-0 flex-1">{render(it)}</div>
            {canEdit && (
              <div className="flex shrink-0 gap-1">
                <button onClick={() => onEdit(it)} className="rounded-lg p-1.5 text-ink-soft hover:bg-stone-100" title={t("Edit")} data-testid={`edit-${testid}-${it.name}`}><Pencil size={14} /></button>
                <button onClick={() => onRemove(it)} className="rounded-lg p-1.5 text-danger hover:bg-danger-light" title={t("Remove")} data-testid={`remove-${testid}-${it.name}`}><Trash2 size={14} /></button>
              </div>
            )}
          </Card>
        ))}
      </div>
      )}
    </>
  );
}


/* Step 4D UX — prominent Classification + Visibility section for the Product form */
function ClassificationVisibility({ form, setForm, products, t }) {
  const current = form.product_type || "RAW_MATERIAL";
  const apply = (type) => setForm((f) => ({ ...f, product_type: type, ...(CLASSIFICATION[type]?.preset || {}) }));

  return (
    <div className="col-span-2 rounded-2xl border-2 border-primary/15 bg-primary-light/40 p-4" data-testid="pf-classification-section">
      <div className="mb-1 flex items-center gap-2">
        <Layers size={16} className="text-primary" />
        <h4 className="font-heading text-sm font-extrabold text-ink">{t("Classification & Visibility")}</h4>
      </div>
      <p className="mb-3 text-xs text-ink-mute">{t("Select a classification to auto-fill visibility (you can still override).")}</p>

      {/* 3 classification cards */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {Object.entries(CLASSIFICATION).map(([type, c]) => {
          const active = current === type;
          const Icon = c.icon;
          return (
            <button key={type} type="button" onClick={() => apply(type)} data-testid={`pf-class-card-${type}`}
              className={`rounded-xl border-2 p-3 text-left transition-all ${active ? "border-primary bg-white shadow-sm" : "border-transparent bg-white/60 hover:bg-white"}`}>
              <div className="mb-2 flex items-center gap-2">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${active ? "bg-primary text-white" : "bg-stone-100 text-ink-soft"}`}><Icon size={16} /></span>
                <span className="text-sm font-bold leading-tight text-ink">{t(c.label)}</span>
              </div>
              <ul className="space-y-1">
                {VIS_MODULES.map((m) => {
                  const on = c.preset[m.key];
                  return (
                    <li key={m.key} className="flex items-center gap-1.5 text-[11px]">
                      {on ? <Check size={12} className="shrink-0 text-success-text" /> : <X size={12} className="shrink-0 text-ink-mute" />}
                      <span className={on ? "text-ink-soft" : "text-ink-mute line-through"}>{t(m.short)}</span>
                    </li>
                  );
                })}
              </ul>
            </button>
          );
        })}
      </div>

      {/* Ready-to-Cook Ingredient — Raw → Ready-to-Cook standard */}
      {current === "PREPARED" && (
        <div className="mt-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3" data-testid="pf-prep-config">
          <div className="mb-1 flex items-center gap-1.5 text-sm font-bold text-emerald-800"><Sprout size={14} /> {t("Preparation Standard")}</div>
          <p className="mb-2 text-[11px] text-emerald-700">{t("Master Data sets the expected standard only — actual yield is decided at Meja Prepare.")}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <label className="cf-label">{t("Source Raw Material")}</label>
              <select value={form.prep_source_product_id || ""} onChange={(e) => setForm({ ...form, prep_source_product_id: e.target.value })} className="cf-input" data-testid="pf-prep-source">
                <option value="">{t("Select source raw material")}</option>
                {(products || []).filter((p) => (p.product_type === "RAW_MATERIAL" || !p.product_type) && p.id !== form.id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className="cf-label">{t("Expected Yield (%)")}</label>
              <input type="number" min="1" max="100" value={form.expected_yield_pct ?? ""} onChange={(e) => setForm({ ...form, expected_yield_pct: e.target.value === "" ? null : Number(e.target.value) })} className="cf-input" data-testid="pf-prep-yield" />
            </div>
            <div>
              <label className="cf-label">{t("Yield Tolerance (%)")}</label>
              <input type="number" min="0" max="50" value={form.yield_tolerance_pct ?? ""} onChange={(e) => setForm({ ...form, yield_tolerance_pct: e.target.value === "" ? null : Number(e.target.value) })} className="cf-input" data-testid="pf-prep-tolerance" />
            </div>
          </div>
          <p className="mt-2 text-[11px] text-emerald-700">{t("This Ready-to-Cook ingredient appears in Meja Prepare automatically once a source raw material is set.")}</p>
        </div>
      )}

      {/* Visibility toggles */}
      <div className="mt-4 rounded-xl bg-white/70 p-3" data-testid="pf-visibility">
        <label className="cf-label mb-2 block">{t("Visibility")}</label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {VIS_MODULES.map((m) => (
            <label key={m.key} className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={form[m.key] !== false}
                onChange={(e) => setForm({ ...form, [m.key]: e.target.checked })}
                className="h-4 w-4 accent-primary" data-testid={`pf-${m.key}`} />
              {t(m.label)}
            </label>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink-mute" data-testid="pf-visibility-helper">{t("Unchecked products will not appear in that module.")}</p>
      </div>

      {/* Fresh Market (perishable) flag — routes consolidated demand to the Fresh Market queue */}
      <label className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-medium" data-testid="pf-perishable-row">
        <input type="checkbox" checked={!!form.is_perishable}
          onChange={(e) => setForm({ ...form, is_perishable: e.target.checked })}
          className="h-4 w-4 accent-emerald-600" data-testid="pf-is_perishable" />
        <span>{t("Fresh Market item (perishable)")}</span>
        <span className="ml-auto text-[11px] text-emerald-700">{t("Goes to the Fresh Market purchasing queue")}</span>
      </label>

      {/* Central Kitchen workflow hint */}
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="pf-workflow-hint">
        <div className="mb-1.5 flex items-center gap-1.5 font-bold"><Info size={13} /> {t("Example:")}</div>
        <div className="space-y-0.5 font-medium">
          <div>Susu &rarr; {t("Central Raw Material")}</div>
          <div>Margarin &rarr; {t("Central Raw Material")}</div>
          <div>Saos Buttermilk &rarr; {t("Central Production Product")}</div>
        </div>
        <div className="mt-2 font-semibold">{t("When production runs:")}</div>
        <ul className="ml-4 list-disc space-y-0.5">
          <li>{t("Susu & Margarin stock decreases")}</li>
          <li>{t("Saos Buttermilk stock increases")}</li>
        </ul>
        <p className="mt-2">{t("Branches can only see and order Saos Buttermilk.")}</p>
      </div>
    </div>
  );
}


/* ============================ Products Catalog (filter + sort + search) ============================
 * Master Data usability upgrade: Category filter, Classification filter, sort by multiple keys,
 * and a category-aware search (product name OR category name).
 */
const SORT_OPTIONS = [
  { key: "category_asc", label: "Category (A-Z)" },
  { key: "category_desc", label: "Category (Z-A)" },
  { key: "name_asc", label: "Name (A-Z)" },
  { key: "name_desc", label: "Name (Z-A)" },
  { key: "class_asc", label: "Classification (A-Z)" },
  { key: "class_desc", label: "Classification (Z-A)" },
  { key: "updated_desc", label: "Updated Date (Newest)" },
  { key: "updated_asc", label: "Updated Date (Oldest)" },
  { key: "price_asc", label: "Price (Low to High)" },
  { key: "price_desc", label: "Price (High to Low)" },
];

function ProductsCatalog({ products, categories, canEdit,
    search, setSearch, categoryFilter, setCategoryFilter,
    classFilter, setClassFilter, sort, setSort,
    openProduct, archive, hardDelete, t }) {

  const filteredSorted = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    let arr = products.filter((p) => {
      if (categoryFilter !== "ALL" && p.category_id !== categoryFilter) return false;
      if (classFilter !== "ALL" && (p.product_type || "RAW_MATERIAL") !== classFilter) return false;
      if (!q) return true;
      // Category-aware search: match product name OR its category name OR classification label.
      const catName = (p.category_name || "").toLowerCase();
      const clsLabel = (CLASSIFICATION[p.product_type || "RAW_MATERIAL"]?.label || "").toLowerCase();
      return (p.name || "").toLowerCase().includes(q) || catName.includes(q) || clsLabel.includes(q);
    });
    const catNameOf = (p) => p.category_name || "";
    const clsLabelOf = (p) => CLASSIFICATION[p.product_type || "RAW_MATERIAL"]?.label || "";
    const cmp = {
      category_asc: (a, b) => catNameOf(a).localeCompare(catNameOf(b)) || a.name.localeCompare(b.name),
      category_desc: (a, b) => catNameOf(b).localeCompare(catNameOf(a)) || a.name.localeCompare(b.name),
      name_asc: (a, b) => (a.name || "").localeCompare(b.name || ""),
      name_desc: (a, b) => (b.name || "").localeCompare(a.name || ""),
      class_asc: (a, b) => clsLabelOf(a).localeCompare(clsLabelOf(b)) || a.name.localeCompare(b.name),
      class_desc: (a, b) => clsLabelOf(b).localeCompare(clsLabelOf(a)) || a.name.localeCompare(b.name),
      updated_desc: (a, b) => (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || ""),
      updated_asc: (a, b) => (a.updated_at || a.created_at || "").localeCompare(b.updated_at || b.created_at || ""),
      price_asc: (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0),
      price_desc: (a, b) => (Number(b.price) || 0) - (Number(a.price) || 0),
    }[sort] || ((a, b) => 0);
    arr.sort(cmp);
    return arr;
  }, [products, search, categoryFilter, classFilter, sort]);

  return (
    <>
      {/* Toolbar: search + category filter + classification filter + sort */}
      <Card className="mb-3 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-12">
          <div className="relative md:col-span-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="md-products-search"
              placeholder={t("Search product or category…")}
              className="w-full rounded-xl border border-line bg-white py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none" />
          </div>
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="cf-input md:col-span-3" data-testid="md-category-filter">
            <option value="ALL">{t("All Categories")}</option>
            {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} className="cf-input md:col-span-3" data-testid="md-class-filter">
            <option value="ALL">{t("All Classifications")}</option>
            {Object.entries(CLASSIFICATION).map(([key, cls]) => (
              <option key={key} value={key}>{t(cls.label)}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="cf-input md:col-span-2" data-testid="md-sort">
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key}>{t("Sort by:")} {t(o.label)}</option>
            ))}
          </select>
        </div>
        <p className="mt-2 text-xs text-ink-mute" data-testid="md-result-count">
          {filteredSorted.length} / {products.length} {t("products")}
        </p>
      </Card>

      {filteredSorted.length === 0 ? (
        <EmptyState title={t("No matching products")} subtitle={t("Try clearing filters or the search box.")} />
      ) : (
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
              <th className="px-4 py-3">{t("Product")}</th><th className="px-4 py-3">{t("Classification")}</th><th className="hidden px-4 py-3 lg:table-cell">{t("Category")}</th><th className="hidden px-4 py-3 sm:table-cell">{t("Unit")}</th>
              <th className="hidden px-4 py-3 text-right md:table-cell">{t("Min/Ideal")}</th><th className="px-4 py-3 text-right">{t("Price")}</th>
              <th className="px-4 py-3 text-center">{t("Active")}</th>{canEdit && <th className="px-4 py-3"></th>}</tr></thead>
            <tbody>
              {filteredSorted.map((p) => {
                const cc = CATEGORY_COLORS[p.category_color] || CATEGORY_COLORS.packaging;
                const cls = CLASSIFICATION[p.product_type] || CLASSIFICATION.RAW_MATERIAL;
                const ClsIcon = cls.icon;
                return (
                  <tr key={p.id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`product-row-${p.name}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <ProductAvatar image={p.image} colorKey={p.category_color} name={p.name} size="sm" />
                        <span className="font-semibold text-ink">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><Badge tone={cls.tone} data-testid={`classification-${p.name}`}><span className="inline-flex items-center gap-1"><ClsIcon size={12} /> {t(cls.label)}</span></Badge></td>
                    <td className="hidden px-4 py-3 lg:table-cell"><span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: cc.bg, color: cc.text }}>{p.category_name}</span></td>
                    <td className="hidden px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-ink-mute sm:table-cell">{fmtUnit(p.unit)}</td>
                    <td className="hidden px-4 py-3 text-right text-ink-soft md:table-cell">{p.minimum_stock}/{p.ideal_stock}</td>
                    <td className="px-4 py-3 text-right">{fmtRp(p.price)}</td>
                    <td className="px-4 py-3 text-center">{p.active ? <Badge tone="success">{t("Active")}</Badge> : <Badge tone="mute">{t("Inactive")}</Badge>}</td>
                    {canEdit && <td className="px-4 py-3"><div className="flex gap-1">
                      <button onClick={() => openProduct(p)} className="rounded-lg p-1.5 text-ink-soft hover:bg-stone-100" data-testid={`edit-${p.name}`}><Pencil size={15} /></button>
                      <button onClick={() => archive(p)} className="rounded-lg p-1.5 text-warn-text hover:bg-warn-light" title={t("Archived")} data-testid={`archive-${p.name}`}><Archive size={15} /></button>
                      <button onClick={() => hardDelete(p)} className="rounded-lg p-1.5 text-danger hover:bg-danger-light" title={t("Delete")} data-testid={`delete-${p.name}`}><Trash2 size={15} /></button>
                    </div></td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </>
  );
}
